import { FiCode } from 'react-icons/fi';

export const passiveVoiceData = {
  courseId: 'passive-voice',
  courseType: 'grammar',
  title: 'Passive Voice',
  icon: FiCode,
  overview: {
    impact: 'Essential for Task 1 processes and formal writing',
    focus: 'Shifting focus from doer to action',
    uses: 'Processes, formal descriptions, unknown agents',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'What Is Passive Voice?',
      content: `<p>Passive voice shifts focus from the doer to the action or receiver of the action. It's especially useful when the action is more important than who performed it.</p>
      <h4>Formation:</h4>
      <p><strong>Subject + be (tense) + past participle</strong></p>
      <p>Optional: Add "by + doer" at the end when the agent is important.</p>`,
      examples: [
        { label: 'Active', text: 'The workers process the materials.' },
        { label: 'Passive', text: 'The materials are processed (by the workers).' },
      ],
    },
    {
      title: 'Passive Voice in Different Tenses',
      content: `<p>The passive voice can be formed in all major tenses. The key is to use the correct form of "be" plus the past participle.</p>`,
      table: {
        headers: ['Tense', 'Active', 'Passive'],
        rows: [
          ['Present Simple', 'They clean the room.', 'The room is cleaned.'],
          ['Past Simple', 'They cleaned the room.', 'The room was cleaned.'],
          ['Present Perfect', 'They have cleaned the room.', 'The room has been cleaned.'],
          ['Future', 'They will clean the room.', 'The room will be cleaned.'],
          ['Present Continuous', 'They are cleaning the room.', 'The room is being cleaned.'],
        ],
      },
    },
    {
      title: 'When to Use Passive Voice',
      content: `<p>Knowing when to use passive voice is crucial for IELTS success. Here are the key situations:</p>`,
      points: [
        'When the doer is unknown: "My phone was stolen." (We don\'t know who)',
        'When the doer is unimportant: "The windows are cleaned weekly."',
        'In formal writing (especially IELTS Task 1): "The data is collected monthly."',
        'When describing processes: "The materials are processed, packaged, and distributed."',
      ],
      warning: 'Don\'t overuse passive voice in Task 2 essays. Balance it with active voice for natural writing.',
    },
  ],
  exercises: [
    {
      title: 'Choose the Correct Form',
      type: 'select',
      instructions: 'Select the correct passive form for each sentence.',
      questions: [
        {
          text: 'Active: The workers process the materials.\nPassive: The materials ___.',
          options: ['are processed', 'is processed', 'were processed'],
          answer: 'are processed',
          explanation: 'Present simple passive: "are" + past participle for plural subjects.',
        },
        {
          text: 'Active: They cleaned the laboratory yesterday.\nPassive: The laboratory ___.',
          options: ['is cleaned', 'was cleaned', 'were cleaned'],
          answer: 'was cleaned',
          explanation: 'Past simple passive: "was" for singular subjects.',
        },
        {
          text: 'Active: The company will launch the product.\nPassive: The product ___.',
          options: ['will be launched', 'is launched', 'was launched'],
          answer: 'will be launched',
          explanation: 'Future passive: "will be" + past participle.',
        },
        {
          text: 'Active: He has repaired the machine.\nPassive: The machine ___.',
          options: ['has been repaired', 'was repaired', 'is repaired'],
          answer: 'has been repaired',
          explanation: 'Present perfect passive: "has been" + past participle.',
        },
        {
          text: 'Active: Someone is testing the new system.\nPassive: The new system ___.',
          options: ['is being tested', 'is tested', 'was being tested'],
          answer: 'is being tested',
          explanation: 'Present continuous passive: "is being" + past participle.',
        },
        {
          text: 'Active: The committee had approved the budget.\nPassive: The budget ___.',
          options: ['had been approved', 'was approved', 'has been approved'],
          answer: 'had been approved',
          explanation: 'Past perfect passive: "had been" + past participle.',
        },
      ],
    },
    {
      title: 'Identify the Tense',
      type: 'select',
      instructions: 'What tense is used in each passive sentence?',
      questions: [
        {
          text: 'The report is being written by the team.',
          options: ['Present Continuous', 'Present Simple', 'Past Continuous'],
          answer: 'Present Continuous',
          explanation: '"Is being written" = present continuous passive.',
        },
        {
          text: 'The new hospital had been opened before the pandemic.',
          options: ['Past Perfect', 'Past Simple', 'Present Perfect'],
          answer: 'Past Perfect',
          explanation: '"Had been opened" = past perfect passive.',
        },
        {
          text: 'Fresh vegetables are delivered every morning.',
          options: ['Present Simple', 'Present Continuous', 'Present Perfect'],
          answer: 'Present Simple',
          explanation: '"Are delivered" = present simple passive (for routines).',
        },
      ],
    },
    {
      title: 'Active or Passive?',
      type: 'select',
      instructions: 'Which voice is better for each situation?',
      questions: [
        {
          text: 'Describing a factory process in Task 1:',
          options: ['Passive', 'Active'],
          answer: 'Passive',
          explanation: 'Passive is ideal for processes: "The raw materials are processed..."',
        },
        {
          text: 'When the doer is unknown:',
          options: ['Passive', 'Active'],
          answer: 'Passive',
          explanation: 'Use passive when we don\'t know who did it: "My car was stolen."',
        },
        {
          text: 'When you want to emphasize who did something:',
          options: ['Active', 'Passive'],
          answer: 'Active',
          explanation: 'Active emphasizes the doer: "The CEO announced the new policy."',
        },
      ],
    },
    {
      title: 'Convert to Passive Voice',
      type: 'writing',
      instructions: 'Write the passive form of each sentence.',
      questions: [
        {
          text: 'They are building a new bridge.',
          example: 'A new bridge is being built.',
          tip: 'Present continuous passive: "is/are being" + past participle.',
        },
        {
          text: 'The police interviewed the suspect.',
          example: 'The suspect was interviewed by the police.',
          tip: 'Past simple passive. Add "by" only if the agent is important.',
        },
        {
          text: 'Scientists have discovered a new planet.',
          example: 'A new planet has been discovered (by scientists).',
          tip: 'Present perfect passive: "has/have been" + past participle.',
        },
        {
          text: 'Workers pack the products in boxes.',
          example: 'The products are packed in boxes (by workers).',
          tip: 'Present simple passive for processes.',
        },
      ],
    },
    {
      title: 'Task 1 Process Description',
      type: 'writing',
      instructions: 'Complete this process description using passive voice.',
      questions: [
        {
          text: 'First, raw materials (collect) from suppliers. Then, they (transport) to the factory.',
          example: 'First, raw materials are collected from suppliers. Then, they are transported to the factory.',
          tip: 'Use present simple passive for describing processes.',
        },
        {
          text: 'After the materials (process), the finished products (inspect) for quality.',
          example: 'After the materials are processed, the finished products are inspected for quality.',
          tip: 'Keep the same tense throughout the process description.',
        },
      ],
    },
  ],
  tips: [
    'Learn common irregular past participles (written, done, built)',
    'Practice converting active to passive in all tenses',
    'Use passive voice in Task 1 process descriptions',
    'Balance active and passive voice in Task 2 essays',
  ],
  dailyPractice: [
    'Convert news headlines to passive voice',
    'Write process descriptions using passive',
    'Practice with different tenses',
    'Review irregular past participles',
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar Concepts',
  aiContext: 'Passive Voice: Formed with "be + past participle", used when the action is more important than the doer, or when the doer is unknown/obvious. Essential for IELTS Writing Task 1 (processes, maps) and formal writing.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...passiveVoiceData,
      title: passiveVoiceData.title + ' (Foundations)',
      overview: {
        ...passiveVoiceData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: passiveVoiceData.sections.slice(0, Math.max(2, Math.ceil(passiveVoiceData.sections.length / 2))),
      exercises: passiveVoiceData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...passiveVoiceData,
      title: passiveVoiceData.title + ' (Advanced)',
      overview: {
        ...passiveVoiceData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: passiveVoiceData.sections,
      exercises: passiveVoiceData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(passiveVoiceData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return passiveVoiceData;
}
